import type { RecordOutcome } from '../../app/matchRecord';
import { MATCH_STATE } from '../../domain/machine';
import { ARTIFACT_PHASE } from '../../types';
import type { CoachCardContext, CoachCardRow, MatchPurpose, MatchState } from '../../types';
import { framingForB } from '../card/cardB';

/**
 * B-18 — the **match record's pure decisions** (design spec §2.4; §6/F-1; scenarios 30–33).
 *
 * DOM-free, so the Node suite drives every arm; `MatchRecordScreen` is rendering over these.
 * Four kinds of question live here, and each is a rule a screenshot cannot settle:
 *
 * 1. **What stands between this match and being complete** — F-1's two guards, mirrored so the
 *    screen can name each blocker and offer its one-tap resolution *before* the button is pressed.
 * 2. **Which of the record's controls this state still permits.** Three engine windows reach this
 *    screen (card drafts, boundary adjustment, flag triage) and every one of them is narrower than
 *    "the record is open". A control that exists only to be refused is a mistap manufactured on
 *    chrome (the B-14 rule), so the windows are mirrored — and `recordB.test.ts` drives the REAL
 *    handlers across all nine states to prove each mirror still agrees with the engine.
 * 3. **What the full-time door is called** — from `framingForB`, never a fourth name.
 * 4. **Which outcomes were actually seen**, so a record does not open on forty-nine zeroes.
 *
 * ── Why a mirror at all, when the engine already refuses ────────────────────
 *
 * `app/triage.ts` states the pairing for `PassTriage` and it is the same here: *"the disable is
 * what the user reads, the guard is what makes it true when a resolve fails or a second surface
 * raced this one."* Two expressions of one rule is a drift risk, which is why the agreement is a
 * test rather than a comment.
 */

// ── F-1's blockers (spec §6; §2.4's "the UI walks the user through both") ────

/**
 * One thing standing between the match and `archived`, in the order the engine refuses them.
 *
 * A draft blocker carries its own card, because its resolution is *that* card's workflow — and
 * because the halftime and full-time drafts are different pieces of unfinished business that the
 * user resolves in different places.
 */
export type CompletionBlockerB =
  | { readonly kind: 'flags'; readonly count: number }
  | { readonly kind: 'draft'; readonly context: CoachCardContext; readonly cardId: string };

/**
 * §6 — everything blocking completion, flags first.
 *
 * Flags first because that is the order `machine.transition` refuses in: a match with both would
 * be refused for its flags, so a screen that led with the draft would name a blocker that is not
 * the one the next press will hit.
 */
export function completionBlockersB(
  unresolvedFlagCount: number,
  cards: readonly CoachCardRow[],
): readonly CompletionBlockerB[] {
  const blockers: CompletionBlockerB[] = [];
  if (unresolvedFlagCount > 0) blockers.push({ kind: 'flags', count: unresolvedFlagCount });
  for (const card of cards) {
    if (card.status !== 'draft') continue;
    blockers.push({ kind: 'draft', context: card.context, cardId: card.id });
  }
  return blockers;
}

/**
 * §6 — may *Complete match* be offered right now?
 *
 * The state term is not defensiveness: `fullTime` is the only `from` the table has for
 * `CompleteMatch`, so an archived match (or an imported A match sitting in `triage`) must not be
 * offered a control that cannot fire.
 */
export function canCompleteB(
  state: MatchState,
  unresolvedFlagCount: number,
  cards: readonly CoachCardRow[],
): boolean {
  if (state !== MATCH_STATE.fullTime) return false;
  return completionBlockersB(unresolvedFlagCount, cards).length === 0;
}

// ── The three engine windows this screen has to respect ──────────────────────

/**
 * SES-8's window (`handleAdjustBoundaries`): *"once the match has finished"* — `fullTime`,
 * `triage`, `distill`. **Not `archived`**: the anchors of an archived match are settled, and
 * B never enters the two middle states but an imported A archive can hold them (scenario 35).
 */
export function canAdjustClockB(state: MatchState): boolean {
  return (
    state === MATCH_STATE.fullTime ||
    state === MATCH_STATE.triage ||
    state === MATCH_STATE.distill
  );
}

/**
 * DIST-1's window (`handleResolveFlag`): `firstHalf` through `distill`. On the record that means
 * everything except `archived` — triage is over once the match is complete, which is exactly what
 * F-1's first guard guarantees before it lets the match get there.
 */
export function canTriageFlagsB(state: MatchState): boolean {
  return (
    state === MATCH_STATE.firstHalf ||
    state === MATCH_STATE.halftime ||
    state === MATCH_STATE.secondHalf ||
    state === MATCH_STATE.fullTime ||
    state === MATCH_STATE.triage ||
    state === MATCH_STATE.distill
  );
}

// ── The full-time door (scenario 31; §2.5's framing table) ───────────────────

/** The record's workflow entry: what it opens, and the name §2.5 gives that card. */
export interface FullTimeDoorB {
  readonly context: CoachCardContext;
  /** §2.4 — *"Build full-time review / Build points of emphasis"*, from the framing, not restated. */
  readonly label: string;
}

/**
 * Scenario 31 — the full-time workflow entry, for **both purposes**, or `null`.
 *
 * The window is `fullTime` exactly: `UpsertCoachCardDraft` refuses a `fullTime` card in any other
 * state (`ERR_CARD_WINDOW`), so an archived record offers no door — its cards are history.
 *
 * The label is `framingForB`'s title, lowercased into the sentence. Deriving it is what stops the
 * button and the card it produces from disagreeing about what was built: the two full-time
 * framings differ only by purpose, and a hand-written label would be a second place for that
 * distinction to live. A match with no purpose gets an honest generic — the card it makes will
 * render unnamed for the same reason (`CoachCardView`'s null arm), and inventing a fourth name
 * here to cover it is precisely what scenario 32's negative forbids.
 */
export function fullTimeDoorB(purpose: MatchPurpose | null, state: MatchState): FullTimeDoorB | null {
  if (state !== MATCH_STATE.fullTime) return null;
  const framing = framingForB(purpose, ARTIFACT_PHASE.fullTime);
  return {
    context: ARTIFACT_PHASE.fullTime,
    label: framing === null ? 'Build the full-time card' : `Build ${framing.title.toLowerCase()}`,
  };
}

// ── The outcome list (scenario 33) ───────────────────────────────────────────

/** The record's two groups of pattern. Both are outcomes; only one is worth opening on. */
export interface OutcomeSplitB {
  /** Anything the match actually recorded, in the order `loadMatchRecord` already sorted them. */
  readonly observed: readonly RecordOutcome[];
  /**
   * Carried onto the board and never seen. Kept, not dropped: under PRE-9 a match starts with the
   * whole ~49-pattern library, so "we watched for this and it did not happen" is most of what a
   * scouting record says — and a pattern the user deliberately marked *invalidated* without ever
   * recording one lives here too.
   */
  readonly unseen: readonly RecordOutcome[];
}

/**
 * Split on `cachedCount > 0` — **the same line `loadWorkflowContext` draws** for "what did you
 * see?", so the record and the adjustments workflow cannot disagree about which patterns the match
 * observed. Order is preserved from the caller; this never re-sorts.
 */
export function observedSplitB(outcomes: readonly RecordOutcome[]): OutcomeSplitB {
  return {
    observed: outcomes.filter((one) => one.hypothesis.cachedCount > 0),
    unseen: outcomes.filter((one) => one.hypothesis.cachedCount === 0),
  };
}
